import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { ConfigProvider } from "./contexts/ConfigContext";
import { AuthProvider } from "./contexts/AuthContext";
import { EventProvider } from "./contexts/EventContext";
import { ErrorBoundary, NotificationProvider } from "./components/common";
import Navigation from "./components/navigation/Navigation";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import GroupProtectedRoute from "./components/routing/GroupProtectedRoute";
import AuthRouteManager from "./components/routing/AuthRouteManager";
import UserbackWidget from "./components/UserbackWidget";
import GoogleAnalytics from "./components/GoogleAnalytics";
import AnalyticsPageTracker from "./components/AnalyticsPageTracker";
import { useNewRelic } from "./hooks/useNewRelic";

import HomePage from "./pages/HomePage";
import HowItWorksPage from "./pages/HowItWorksPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MagicLinkVerifyPage from "./pages/MagicLinkVerifyPage";
import GroupSelectionPage from "./pages/GroupSelectionPage";

import GroupSetupPage from "./pages/GroupSetupPage";
import AdminPage from "./pages/AdminPage";
import MyListsPage from "./pages/MyListsPage";
import BrowseListsPage from "./pages/BrowseListsPage";
import MyReservationsPage from "./pages/MyReservationsPage";

function App() {
  // Initialize New Relic Browser Agent
  useNewRelic();

  return (
    <ErrorBoundary>
      <ConfigProvider>
        <NotificationProvider>
          <AuthProvider>
            <Router>
              <ScrollToTop />
              <AuthRouteManager />
              <GoogleAnalytics />
              <EventProvider>
                <UserbackWidget />
                <AnalyticsPageTracker />
                <div className="App">
                  <Navigation />
                  <main>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/come-funziona" element={<HowItWorksPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/termini" element={<TermsPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route
                        path="/create-group"
                        element={<GroupSetupPage />}
                      />
                      <Route
                        path="/auth/verify/:token"
                        element={<MagicLinkVerifyPage />}
                      />
                      <Route
                        path="/group-selection"
                        element={<GroupSelectionPage />}
                      />

                      {/* Legacy protected routes (redirect to group-based URLs) */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/group-selection" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/my-lists"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/group-selection" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/browse-lists"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/group-selection" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/my-reservations"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/group-selection" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/group-selection" replace />
                          </ProtectedRoute>
                        }
                      />

                      {/* Group-based protected routes */}
                      <Route
                        path="/:groupSlug/dashboard"
                        element={
                          <GroupProtectedRoute>
                            <DashboardPage />
                          </GroupProtectedRoute>
                        }
                      />
                      <Route
                        path="/:groupSlug/my-lists"
                        element={
                          <GroupProtectedRoute>
                            <MyListsPage />
                          </GroupProtectedRoute>
                        }
                      />
                      <Route
                        path="/:groupSlug/browse-lists"
                        element={
                          <GroupProtectedRoute>
                            <BrowseListsPage />
                          </GroupProtectedRoute>
                        }
                      />
                      <Route
                        path="/:groupSlug/my-reservations"
                        element={
                          <GroupProtectedRoute>
                            <MyReservationsPage />
                          </GroupProtectedRoute>
                        }
                      />
                      <Route
                        path="/:groupSlug/admin"
                        element={
                          <GroupProtectedRoute>
                            <AdminPage />
                          </GroupProtectedRoute>
                        }
                      />

                      {/* Catch all route */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </main>
                </div>
              </EventProvider>
            </Router>
          </AuthProvider>
        </NotificationProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
